import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { isChrome } from 'react-device-detect'
import { QrReader } from 'react-qr-reader'

import { scannerStatuses, statusToColor } from './utils'
import { ScannerOverlay } from './ScannerOverlay'
import {
    StyledPermissionErrorContainer,
    StyledPermissionErrorTextContainer
} from './styles'
import ketchup from 'shared/assets/images/ketchup.png'
import { H3, Text } from '../typography'

export type ScannerProps = {
    onClose: () => void,
    onScan: (result: string) => Promise<void>,
    helpText?: string,
    idleColor?: string
}

export const Scanner = ({
    onClose,
    onScan,
    helpText,
    idleColor = 'white'
}: ScannerProps) => {
    const intl = useIntl()

    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState(scannerStatuses.NORMAL)
    const [showErrorPermission, setShowErrorPermission] = useState(false)
    const [lastError, setLastError] = useState(null)

    const processing = useRef(false)

    if (showErrorPermission) {
        return (
            <StyledPermissionErrorContainer>
                <img src={ketchup} alt="Oups" />
                <StyledPermissionErrorTextContainer>
                    <H3 center>
                        {intl.formatMessage({ id: 'settings.activate.camera' })}
                    </H3>
                    <Text center>
                        {intl.formatMessage({
                            id: isChrome
                                ? 'qr.code.scanner.howto.chrome'
                                : 'qr.code.scanner.howto.safari'
                        })}
                    </Text>
                </StyledPermissionErrorTextContainer>
            </StyledPermissionErrorContainer>
        )
    }

    return (
        <QrReader
            scanDelay={500}
            constraints={{
                facingMode: { ideal: 'environment' }
            }}
            onResult={async (result, error) => {
                const isCameraError =
                    !result &&
                    (error.name === 'NotAllowedError' || // utilisateur a bloqué la caméra
                    error.name === 'NotFoundError' || // pas de caméra dispo
                        error.name === 'NotReadableError') // caméra occupée

                if (isCameraError) {
                    setShowErrorPermission(true)
                    return
                }

                if (
                    !result ||
                    status !== scannerStatuses.NORMAL ||
                    processing.current
                ) {
                    return
                }

                processing.current = true
                setLoading(true)

                try {
                    await onScan(result.getText())
                    setStatus(scannerStatuses.SUCCESS)
                    setTimeout(onClose, 750)
                } catch (e) {
                    setStatus(scannerStatuses.ERROR)
                    setLastError('message' in e ? e.message : JSON.stringify(e))
                    setTimeout(() => {
                        setStatus(scannerStatuses.NORMAL)
                        setLastError(null)
                    }, 2500)
                } finally {
                    setLoading(false)
                    processing.current = false
                }
            }}
            containerStyle={{
                paddingTop: 0,
                width: '100%',
                overflow: 'hidden',
                position: 'absolute',
                top: 54,
                bottom: 0,
                left: 0,
                right: 0,
                background: 'black'
            }}
            videoStyle={{
                objectFit: 'cover',
                zIndex: 1
            }}
            ViewFinder={() => (
                <ScannerOverlay
                    color={statusToColor(status, idleColor)}
                    error={lastError}
                    loading={loading}
                    helpText={helpText}
                />
            )}
        />
    )
}
